import { Component, OnInit } from '@angular/core';
import { UserInfo } from '../../dtos/user-info';
import { ProfileService } from '../../services/profile.service';
import { ToastService } from '../../services/toast.service';
import { Router } from '@angular/router';
import { PasswordFormValue } from '../password-form/password-form.component';
import { AuthService } from '../../services/auth.service';
import { AdminUser } from '../../dtos/admin-user';
import { AdminUserService } from '../../services/admin-user.service';

@Component({
  selector: 'app-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss'],
  standalone: false,
})
export class ProfileComponent implements OnInit {
  userInfo: UserInfo | null = null;
  loading = true;
  showEditModal = false;
  showChangePasswordModal = false;
  changingPassword = false;
  changePasswordError = '';
  changePasswordSuccess = '';
  showDeactivateAccountConfirm = false;
  deactivatingAccount = false;
  deactivateAccountError = '';
  adminUsers: AdminUser[] = [];
  currentUserEmail = '';
  loadingAdminUsers = false;
  superAdminTransferCandidate: AdminUser | null = null;
  showSuperAdminTransferConfirm = false;
  superAdminTransferConfirmation = '';
  superAdminTransferCurrentPassword = '';
  transferringSuperAdmin = false;
  superAdminTransferError = '';
  changePasswordForm = {
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: '',
  };
  profileSuccessMessage = '';
  private profileSuccessTimeoutId: number | null = null;

  constructor(
    private profileService: ProfileService,
    private toastService: ToastService,
    private authService: AuthService,
    private adminUserService: AdminUserService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.currentUserEmail = this.authService.getUserInfo()?.email ?? '';
    this.loadUserInfo();
  }

  loadUserInfo(): void {
    this.profileService.getCurrentUser(true).subscribe({
      next: (user) => {
        this.userInfo = user;
        this.currentUserEmail = user.email || this.currentUserEmail;
        if (this.isSuperAdmin) {
          this.loadAdminUsers();
        }
      },
      error: (err) => {
        console.error('Error loading user profile:', err);
        this.userInfo = null;
      },
    });
  }

  getInitials(): string {
    return this.userInfo?.initials || 'U';
  }

  get isAdmin(): boolean {
    return ['ADMIN', 'SUPER_ADMIN'].includes(this.currentRole);
  }

  get isSuperAdmin(): boolean {
    return this.currentRole === 'SUPER_ADMIN';
  }

  get currentRole(): string {
    return this.userInfo?.role ?? this.authService.getUserRole();
  }

  get superAdminTransferTargets(): AdminUser[] {
    return this.adminUsers.filter(
      (user) =>
        user.active &&
        user.emailVerified &&
        String(user.id) !== String(this.userInfo?.id ?? '') &&
        user.email.toLowerCase() !== this.currentUserEmail.toLowerCase() &&
        !this.hasSuperAdminRole(user),
    );
  }

  get superAdminTransferConfirmationMatches(): boolean {
    return (
      !!this.superAdminTransferCandidate &&
      this.superAdminTransferConfirmation.trim().toLowerCase() ===
        this.superAdminTransferCandidate.email.toLowerCase()
    );
  }

  get superAdminTransferFormValid(): boolean {
    return (
      this.superAdminTransferConfirmationMatches &&
      this.superAdminTransferCurrentPassword.trim().length > 0
    );
  }

  loadAdminUsers(): void {
    this.loadingAdminUsers = true;
    this.superAdminTransferError = '';

    this.adminUserService.getUsers().subscribe({
      next: (users) => {
        this.adminUsers = users;
        this.loadingAdminUsers = false;
      },
      error: (error) => {
        console.error('Could not load users for Super Admin transfer', error);
        this.loadingAdminUsers = false;
        this.superAdminTransferError = this.extractErrorMessage(
          error,
          'Could not load users for Super Admin transfer.',
        );
      },
    });
  }

  hasSuperAdminRole(user: AdminUser): boolean {
    return user.roles.some((role) => role === 'ROLE_SUPER_ADMIN' || role === 'SUPER_ADMIN');
  }

  formatAdminUserName(user: AdminUser): string {
    const name = `${user.firstName || ''} ${user.lastName || ''}`.trim();
    return name || user.email;
  }

  selectSuperAdminTransferTarget(userId: string): void {
    this.superAdminTransferCandidate =
      this.superAdminTransferTargets.find((user) => user.id === userId) ?? null;
    this.superAdminTransferConfirmation = '';
    this.superAdminTransferCurrentPassword = '';
    this.superAdminTransferError = '';
  }

  askTransferSuperAdmin(): void {
    if (!this.superAdminTransferCandidate) {
      return;
    }

    this.superAdminTransferConfirmation = '';
    this.superAdminTransferCurrentPassword = '';
    this.superAdminTransferError = '';
    this.showSuperAdminTransferConfirm = true;
  }

  cancelTransferSuperAdmin(): void {
    this.showSuperAdminTransferConfirm = false;
    this.superAdminTransferConfirmation = '';
    this.superAdminTransferCurrentPassword = '';
  }

  transferSuperAdmin(): void {
    if (
      !this.superAdminTransferCandidate ||
      !this.superAdminTransferFormValid ||
      this.transferringSuperAdmin
    ) {
      return;
    }

    const target = this.superAdminTransferCandidate;
    this.transferringSuperAdmin = true;
    this.superAdminTransferError = '';

    this.adminUserService
      .transferSuperAdminRole(target.id, this.superAdminTransferCurrentPassword)
      .subscribe({
        next: () => {
          this.transferringSuperAdmin = false;
          this.showSuperAdminTransferConfirm = false;
          this.superAdminTransferCurrentPassword = '';
          this.toastService.success(
            'Super Admin transferred',
            `${this.formatAdminUserName(target)} is now the Super Administrator.`,
          );
          this.authService.logoutUser();
          this.router.navigate(['/login']);
        },
        error: (error) => {
          console.error('Could not transfer Super Admin role', error);
          this.transferringSuperAdmin = false;
          this.showSuperAdminTransferConfirm = false;
          this.superAdminTransferCurrentPassword = '';
          this.superAdminTransferError = this.extractErrorMessage(
            error,
            'Could not transfer the Super Admin role.',
          );
          this.toastService.error('Transfer failed', this.superAdminTransferError);
        },
      });
  }

  openEditProfile(): void {
    this.showEditModal = true;
  }

  closeEditProfile(): void {
    this.showEditModal = false;
  }

  openChangePassword(): void {
    this.resetChangePasswordForm();
    this.showChangePasswordModal = true;
  }

  closeChangePassword(): void {
    this.showChangePasswordModal = false;
    this.resetChangePasswordForm();
  }

  askDeactivateAccount(): void {
    this.deactivateAccountError = '';
    this.showDeactivateAccountConfirm = true;
  }

  cancelDeactivateAccount(): void {
    this.showDeactivateAccountConfirm = false;
  }

  deactivateAccount(): void {
    if (this.deactivatingAccount) {
      return;
    }

    this.deactivatingAccount = true;
    this.deactivateAccountError = '';

    this.profileService.deactivateCurrentUser().subscribe({
      next: () => {
        this.deactivatingAccount = false;
        this.showDeactivateAccountConfirm = false;
        this.toastService.success('Account deactivated', 'Your account has been deactivated.');
        this.authService.logoutUser();
        this.router.navigate(['/login']);
      },
      error: (err) => {
        console.error('Could not deactivate account', err);
        this.deactivatingAccount = false;
        this.showDeactivateAccountConfirm = false;
        this.deactivateAccountError = this.extractErrorMessage(
          err,
          'Could not deactivate your account. Please try again.',
        );
        this.toastService.error('Failed to Deactivate Account', this.deactivateAccountError);
      },
    });
  }

  get newPasswordsDoNotMatch(): boolean {
    return (
      this.changePasswordForm.confirmNewPassword.length > 0 &&
      this.changePasswordForm.newPassword !== this.changePasswordForm.confirmNewPassword
    );
  }

  get newPasswordTooShort(): boolean {
    return (
      this.changePasswordForm.newPassword.length > 0 &&
      this.changePasswordForm.newPassword.length < 8
    );
  }

  get changePasswordFormInvalid(): boolean {
    return (
      !this.changePasswordForm.currentPassword ||
      !this.changePasswordForm.newPassword ||
      !this.changePasswordForm.confirmNewPassword ||
      this.changePasswordForm.newPassword.length < 8 ||
      this.changePasswordForm.newPassword !== this.changePasswordForm.confirmNewPassword
    );
  }

  changePassword(value: PasswordFormValue): void {
    this.changePasswordError = '';
    this.changePasswordSuccess = '';

    if (!value.currentPassword) {
      this.changePasswordError = 'Please enter your old password.';
      return;
    }

    this.changingPassword = true;

    this.profileService
      .changePassword({
        currentPassword: value.currentPassword,
        newPassword: value.newPassword,
      })
      .subscribe({
        next: () => {
          this.changingPassword = false;
          this.changePasswordSuccess = 'Password changed successfully.';
        },
        error: (err) => {
          console.error('Error changing password:', err);
          this.changingPassword = false;
          this.changePasswordError = this.extractErrorMessage(
            err,
            'Could not change password. Please try again.',
          );
        },
      });
  }

  private resetChangePasswordForm(): void {
    this.changePasswordError = '';
    this.changePasswordSuccess = '';
    this.changingPassword = false;
  }

  private extractErrorMessage(error: any, fallback: string): string {
    if (error?.error?.detail) {
      return error.error.detail;
    }

    if (error?.error?.message) {
      return error.error.message;
    }

    if (typeof error?.error === 'string') {
      return error.error;
    }

    if (error?.message) {
      return error.message;
    }

    return fallback;
  }

  saveProfile(updatedUser: UserInfo): void {
    this.userInfo = updatedUser;
    this.closeEditProfile();

    this.profileSuccessMessage =
      'Profile updated. If you changed your email address, please verify the new address before it becomes active.';

    if (this.profileSuccessTimeoutId !== null) {
      window.clearTimeout(this.profileSuccessTimeoutId);
    }

    this.profileSuccessTimeoutId = window.setTimeout(() => {
      this.profileSuccessMessage = '';
      this.profileSuccessTimeoutId = null;
    }, 15000);
  }
}
